<template>
  <div class="admin-tutorial">

    <!-- 다이얼로그 -->
    <div class="dialog" v-if="usingDialog">
      <span class="cls" @click="usingDialog = null"></span>

      <!-- 튜토리얼 수정/추가 -->
      <div class="paper" v-if="usingDialog === 'tutorial'">
        <div class="header">
          <h2>{{ editTutorial.idx ? '튜토리얼 수정' : '튜토리얼 추가' }}</h2>
        </div>
        <div class="form">
          <label>타입 (영문)
            <input type="text" v-model="editTutorial.type" placeholder="welcome, customer, crm 등" :disabled="!!editTutorial.idx" />
          </label>
          <label>제목
            <input type="text" v-model="editTutorial.title" placeholder="튜토리얼 제목" />
          </label>
          <label>설명
            <textarea v-model="editTutorial.description" placeholder="튜토리얼 설명"></textarea>
          </label>
          <label class="checkbox-label">
            <input type="checkbox" v-model="editTutorial.is_active" :true-value="1" :false-value="0" />
            활성화
          </label>
        </div>
        <div class="btns">
          <button v-if="editTutorial.idx" class="del" @click="deleteTutorial">삭제</button>
          <button class="save" @click="saveTutorial">저장</button>
        </div>
      </div>

      <!-- 슬라이드 수정/추가 -->
      <div class="paper" v-if="usingDialog === 'slide'">
        <div class="header">
          <h2>{{ editSlide.idx ? '슬라이드 수정' : '슬라이드 추가' }}</h2>
        </div>
        <div class="form">
          <label>이미지/GIF
            <img :src="editSlide.image_url || '/imgs/placeholder.png'" @click="triggerFileInput" />
            <input type="file" ref="fileInput" hidden accept="image/*" @change="slideOnFileChange" />
            <span class="hint">클릭하여 이미지 업로드 (GIF 가능)</span>
          </label>
          <label>아이콘 (이미지 없을 때)
            <input type="text" v-model="editSlide.icon" placeholder="ph-fill ph-users" />
          </label>
          <label>제목
            <input type="text" v-model="editSlide.title" placeholder="슬라이드 제목" />
          </label>
          <label>내용 (HTML 가능)
            <textarea v-model="editSlide.content" placeholder="슬라이드 내용"></textarea>
          </label>
          <label>순서
            <input type="number" v-model="editSlide.slide_order" placeholder="1, 2, 3..." />
          </label>
          <label class="checkbox-label">
            <input type="checkbox" v-model="editSlide.is_active" :true-value="1" :false-value="0" />
            활성화
          </label>
        </div>
        <div class="btns">
          <button v-if="editSlide.idx" class="del" @click="deleteSlide">삭제</button>
          <button class="save" @click="saveSlide">저장</button>
        </div>
      </div>

    </div>


    <!-- 헤더 -->
    <div class="header">
      <h2>튜토리얼 관리</h2>
    </div>

    <div class="content">
      <!-- 튜토리얼 목록 -->
      <div class="left">
        <div class="header">
          <p>튜토리얼 목록</p>
          <button @click="newTutorial">+ 추가</button>
        </div>
        <ul v-if="tutorialList.length > 0">
          <li 
            v-for="item in tutorialList" 
            :key="item.idx" 
            :class="{ active: selectedTutorial && selectedTutorial.idx === item.idx }"
          >
            <div class="icon-box" @click="selectTutorial(item)">
              <i class="ph-fill ph-presentation-chart"></i>
            </div>
            <div class="description" @click="selectTutorial(item)">
              <h2>{{ item.title }}</h2>
              <span class="type-badge">{{ item.type }}</span>
              <span :class="['status', item.is_active ? 'active' : 'inactive']">
                {{ item.is_active ? '활성' : '비활성' }}
              </span>
            </div>
            <button @click="openEditTutorial(item)">수정</button>
          </li>
        </ul>
        <div v-else class="empty">
          <p>등록된 튜토리얼이 없습니다.</p>
        </div>
      </div>

      <!-- 슬라이드 목록 -->
      <div class="right" v-if="selectedTutorial">
        <div class="header">
          <div>
            <h3>{{ selectedTutorial.title }}</h3>
            <span class="type-badge">{{ selectedTutorial.type }}</span>
          </div>
          <button @click="newSlide">+ 슬라이드 추가</button>
        </div>
        
        <ul v-if="slideList.length > 0">
          <li 
            v-for="slide in slideList" 
            :key="slide.idx"
            @click="openEditSlide(slide)"
            :class="{ inactive: !slide.is_active }"
          >
            <div class="slide-preview">
              <img v-if="slide.image_url" :src="slide.image_url" />
              <div v-else class="icon-placeholder">
                <i :class="slide.icon || 'ph-fill ph-image'"></i>
              </div>
            </div>
            <div class="slide-info">
              <span class="order">#{{ slide.slide_order }}</span>
              <h4>{{ slide.title || '(제목 없음)' }}</h4>
              <p v-html="truncate(slide.content, 50)"></p>
            </div>
            <span :class="['status-dot', slide.is_active ? 'active' : 'inactive']"></span>
          </li>
        </ul>

        <div v-else class="empty">
          <i class="ph ph-slides"></i>
          <p>슬라이드가 없습니다.<br/>슬라이드를 추가해주세요.</p>
        </div>
      </div>

      <!-- 미선택 상태 -->
      <div class="right empty-state" v-else>
        <i class="ph ph-cursor-click"></i>
        <p>좌측에서 튜토리얼을 선택하세요</p>
      </div>
    </div>

  </div>
</template>


<script>
import { inject, ref, onMounted } from "vue";

export default {
  name: "adminTutorial",
  setup() {
    const sql_query = inject("sql_query");
    const getMbid = inject("getMbid");

    const tutorialList = ref([]);
    const slideList = ref([]);
    const selectedTutorial = ref(null);

    const editTutorial = ref({ type: "", title: "", description: "", is_active: 1 });
    const editSlide = ref({ image_url: "", icon: "", title: "", content: "", slide_order: 1, is_active: 1 });
    const usingDialog = ref(null);
    const fileInput = ref(null);

    // SQL 이스케이프
    const escapeSQL = (str) => {
      if (!str) return '';
      return str.replace(/\\/g, '\\\\').replace(/'/g, "''");
    };

    // 텍스트 자르기
    const truncate = (str, len) => {
      if (!str) return '';
      const stripped = str.replace(/<[^>]*>/g, '');
      return stripped.length > len ? stripped.substring(0, len) + '...' : stripped;
    };

    // ---------------------- 튜토리얼 CRUD ----------------------
    async function fetchTutorialList() {
      const res = await sql_query("SELECT * FROM tutorial_info ORDER BY created_at DESC");
      tutorialList.value = res || [];
    }

    function newTutorial() {
      editTutorial.value = { type: "", title: "", description: "", is_active: 1 };
      usingDialog.value = "tutorial";
    }

    function openEditTutorial(item) {
      editTutorial.value = { ...item };
      usingDialog.value = "tutorial";
    }

    async function saveTutorial() {
      if (!editTutorial.value.type || !editTutorial.value.title) {
        alert('타입과 제목을 입력해주세요.');
        return;
      }

      if (editTutorial.value.idx) {
        await sql_query(`
          UPDATE tutorial_info
          SET title='${escapeSQL(editTutorial.value.title)}',
              description='${escapeSQL(editTutorial.value.description)}',
              is_active=${editTutorial.value.is_active ? 1 : 0}
          WHERE idx=${editTutorial.value.idx}
        `);
      } else {
        // 중복 체크
        const existing = await sql_query(`SELECT idx FROM tutorial_info WHERE type='${escapeSQL(editTutorial.value.type)}'`);
        if (existing && existing.length > 0) {
          alert('이미 존재하는 타입입니다.');
          return;
        }
        await sql_query(`
          INSERT INTO tutorial_info (type, title, description, is_active)
          VALUES ('${escapeSQL(editTutorial.value.type)}', 
                  '${escapeSQL(editTutorial.value.title)}', 
                  '${escapeSQL(editTutorial.value.description)}',
                  ${editTutorial.value.is_active ? 1 : 0})
        `);
      }
      usingDialog.value = null;
      fetchTutorialList();
    }

    async function deleteTutorial() {
      if (!editTutorial.value.idx) return;
      const ask = confirm("정말 삭제하시겠습니까?\n이 튜토리얼의 모든 슬라이드도 함께 삭제됩니다.");
      if (!ask) return;

      await sql_query(`DELETE FROM tutorial_slides WHERE type='${escapeSQL(editTutorial.value.type)}'`);
      await sql_query(`DELETE FROM tutorial_info WHERE idx=${editTutorial.value.idx}`);
      
      usingDialog.value = null;
      selectedTutorial.value = null;
      slideList.value = [];
      fetchTutorialList();
    }

    function selectTutorial(item) {
      selectedTutorial.value = item;
      fetchSlideList(item.type);
    }

    // ---------------------- 슬라이드 CRUD ----------------------
    async function fetchSlideList(type) {
      const res = await sql_query(`
        SELECT * FROM tutorial_slides 
        WHERE type='${escapeSQL(type)}' 
        ORDER BY slide_order ASC
      `);
      slideList.value = res || [];
    }

    function newSlide() {
      if (!selectedTutorial.value) return;
      editSlide.value = {
        type: selectedTutorial.value.type,
        image_url: "",
        icon: "",
        title: "",
        content: "",
        slide_order: slideList.value.length + 1,
        is_active: 1
      };
      usingDialog.value = "slide";
    }

    function openEditSlide(slide) {
      editSlide.value = { ...slide };
      usingDialog.value = "slide";
    }

    async function saveSlide() {
      if (!selectedTutorial.value) return;

      if (editSlide.value.idx) {
        await sql_query(`
          UPDATE tutorial_slides
          SET image_url='${escapeSQL(editSlide.value.image_url)}',
              icon='${escapeSQL(editSlide.value.icon)}',
              title='${escapeSQL(editSlide.value.title)}',
              content='${escapeSQL(editSlide.value.content)}',
              slide_order=${editSlide.value.slide_order || 1},
              is_active=${editSlide.value.is_active ? 1 : 0}
          WHERE idx=${editSlide.value.idx}
        `);
      } else {
        await sql_query(`
          INSERT INTO tutorial_slides (type, image_url, icon, title, content, slide_order, is_active)
          VALUES ('${escapeSQL(selectedTutorial.value.type)}',
                  '${escapeSQL(editSlide.value.image_url)}',
                  '${escapeSQL(editSlide.value.icon)}',
                  '${escapeSQL(editSlide.value.title)}',
                  '${escapeSQL(editSlide.value.content)}',
                  ${editSlide.value.slide_order || 1},
                  ${editSlide.value.is_active ? 1 : 0})
        `);
      }
      usingDialog.value = null;
      fetchSlideList(selectedTutorial.value.type);
    }

    async function deleteSlide() {
      if (!editSlide.value.idx) return;
      const ask = confirm("정말 삭제하시겠습니까?");
      if (!ask) return;

      await sql_query(`DELETE FROM tutorial_slides WHERE idx=${editSlide.value.idx}`);
      usingDialog.value = null;
      fetchSlideList(selectedTutorial.value.type);
    }

    // 파일 업로드
    function triggerFileInput() {
      fileInput.value?.click();
    }

    async function slideOnFileChange(e) {
      const file = e.target.files?.[0];
      if (!file) return;

      // 로컬 미리보기
      editSlide.value.image_url = URL.createObjectURL(file);

      // 서버 업로드
      const formData = new FormData();
      formData.append('idx', editSlide.value.idx || '');
      formData.append('table', 'tutorial_slides');
      formData.append('usage', 'image');
      formData.append('user', getMbid());
      formData.append('file', file);

      try {
        const response = await fetch('/api/upload/image', {
          method: 'POST',
          body: formData,
          mode: 'cors',
          credentials: 'include'
        });

        const data = await response.json();

        if (data.url) {
          editSlide.value.image_url = data.url;

          // 수정 모드면 바로 DB 업데이트
          if (editSlide.value.idx) {
            await sql_query(`
              UPDATE tutorial_slides
              SET image_url='${escapeSQL(data.url)}'
              WHERE idx=${editSlide.value.idx}
            `);
          }
        } else {
          alert("이미지 업로드 실패");
        }
      } catch (err) {
        console.error('업로드 오류:', err);
        alert("업로드 중 오류 발생");
      }
    }

    // 초기 로딩
    onMounted(() => {
      fetchTutorialList();
    });

    return {
      tutorialList,
      slideList,
      selectedTutorial,
      editTutorial,
      editSlide,
      usingDialog,
      fileInput,
      truncate,
      fetchTutorialList,
      newTutorial,
      openEditTutorial,
      saveTutorial,
      deleteTutorial,
      selectTutorial,
      fetchSlideList,
      newSlide,
      openEditSlide,
      saveSlide,
      deleteSlide,
      triggerFileInput,
      slideOnFileChange
    };
  },
};
</script>


<style scoped>
.admin-tutorial {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: start;
  align-items: center;
  background-color: var(--color-background);
  gap: 16px;
}

.admin-tutorial > .header {
  width: 100%;
  height: 60px;
  display: flex;
  justify-content: start;
  align-items: center;
  color: var(--color-text-primary);
  border-bottom: 1px solid var(--color-surface-reverse);
  padding: 16px;
  box-sizing: border-box;
}

/* 다이얼로그 */
.dialog {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(2px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.dialog .cls {
  width: 100%;
  height: 100%;
  position: absolute;
  left: 0;
  top: 0;
  z-index: -1;
}

.dialog .paper {
  width: 90%;
  max-width: 500px;
  max-height: 85%;
  background: var(--color-background);
  border: 2px solid var(--color-background-light);
  border-radius: 16px;
  padding: 20px;
  box-sizing: border-box;
  gap: 20px;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
}

.dialog .paper .header {
  border-bottom: 1px solid var(--color-background-light);
  padding-bottom: 12px;
}

.dialog .paper .header h2 {
  margin: 0;
  font-size: 18px;
  color: var(--color-text-primary);
}

.dialog .paper .form {
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.dialog .paper .form > label {
  display: flex;
  flex-direction: column;
  gap: 8px;
  color: var(--color-text-primary);
  font-size: 14px;
}

.dialog .paper .form > label > img {
  width: 100%;
  max-width: 200px;
  aspect-ratio: 16/10;
  object-fit: cover;
  border-radius: 8px;
  background: var(--color-background-light);
  cursor: pointer;
  transition: opacity 0.2s;
}

.dialog .paper .form > label > img:hover {
  opacity: 0.8;
}

.dialog .paper .form > label > .hint {
  font-size: 12px;
  color: var(--color-text-third);
}

.dialog .paper .form > label > input,
.dialog .paper .form > label > textarea,
.dialog .paper .form > label > select {
  width: 100%;
  padding: 12px;
  box-sizing: border-box;
  border-radius: 8px;
  border: 1px solid var(--color-background-light);
  background: var(--color-surface);
  color: var(--color-text-primary);
  font-size: 14px;
}

.dialog .paper .form > label > textarea {
  min-height: 100px;
  resize: vertical;
}

.dialog .paper .form > label.checkbox-label {
  flex-direction: row;
  align-items: center;
  gap: 8px;
}

.dialog .paper .form > label.checkbox-label > input {
  width: auto;
}

.dialog .paper .btns {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  padding-top: 12px;
  border-top: 1px solid var(--color-background-light);
}

.dialog .paper .btns > button {
  flex: 1;
  height: 40px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: #fff;
}

.dialog .paper .btns > button.del {
  background: var(--color-special-warning);
}

.dialog .paper .btns > button.save {
  background: var(--color-brand);
}

/* 컨텐츠 */
.content {
  width: 100%;
  height: 100%;
  flex: 1;
  padding: 16px;
  box-sizing: border-box;
  display: flex;
  justify-content: start;
  align-items: start;
  gap: 16px;
  overflow: hidden;
}

.content > .left {
  width: 320px;
  height: 100%;
  background: var(--color-background-light);
  border-radius: 12px;
  padding: 12px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex-shrink: 0;
}

.content > .left > .header {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 8px 12px;
  box-sizing: border-box;
  border-bottom: 1px solid var(--color-text-secondary);
  color: var(--color-text-primary);
}

.content > .left > .header > p {
  margin: 0;
  font-weight: 600;
}

.content > .left > .header > button,
.content > .right > .header > button {
  padding: 8px 12px;
  border: none;
  border-radius: 6px;
  background: var(--color-brand);
  color: #fff;
  font-size: 13px;
  cursor: pointer;
}

.content > .left > ul {
  width: 100%;
  flex: 1;
  margin: 0;
  padding: 0;
  list-style: none;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.content > .left > ul > li {
  width: 100%;
  padding: 12px;
  box-sizing: border-box;
  border-radius: 8px;
  cursor: pointer;
  color: var(--color-text-primary);
  display: flex;
  justify-content: start;
  align-items: center;
  font-size: 14px;
  gap: 12px;
  transition: background 0.2s;
}

.content > .left > ul > li:hover {
  background: var(--color-surface);
}

.content > .left > ul > li.active {
  background: var(--color-surface);
  border: 1px solid var(--color-brand);
}

.content > .left > ul > li > .icon-box {
  width: 48px;
  height: 48px;
  border-radius: 10px;
  background: var(--color-brand);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.content > .left > ul > li > .icon-box > i {
  font-size: 24px;
  color: #fff;
}

.content > .left > ul > li > .description {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: start;
  flex: 1;
  gap: 4px;
  min-width: 0;
}

.content > .left > ul > li > .description > h2 {
  margin: 0;
  font-size: 15px;
  color: var(--color-text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
}

.content > .left > ul > li > .description > .type-badge {
  font-size: 11px;
  padding: 2px 8px;
  background: var(--color-background);
  border-radius: 4px;
  color: var(--color-text-secondary);
}

.content > .left > ul > li > .description > .status {
  font-size: 11px;
}

.content > .left > ul > li > .description > .status.active {
  color: #22c55e;
}

.content > .left > ul > li > .description > .status.inactive {
  color: var(--color-text-third);
}

.content > .left > ul > li > button {
  padding: 6px 12px;
  border: none;
  border-radius: 6px;
  background: var(--color-surface);
  color: var(--color-text-secondary);
  font-size: 12px;
  cursor: pointer;
  flex-shrink: 0;
}

.content > .left > ul > li > button:hover {
  background: var(--color-brand);
  color: #fff;
}

/* 우측: 슬라이드 목록 */
.content > .right {
  flex: 1;
  height: 100%;
  background: var(--color-background-light);
  border-radius: 12px;
  padding: 16px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow: hidden;
}

.content > .right > .header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--color-text-secondary);
}

.content > .right > .header > div {
  display: flex;
  align-items: center;
  gap: 12px;
}

.content > .right > .header h3 {
  margin: 0;
  font-size: 18px;
  color: var(--color-text-primary);
}

.content > .right > .header .type-badge {
  font-size: 12px;
  padding: 4px 10px;
  background: var(--color-brand);
  border-radius: 4px;
  color: #fff;
}

.content > .right > ul {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
  width: 100%;
  margin: 0;
  padding: 0;
  list-style: none;
  overflow-y: auto;
  flex: 1;
}

.content > .right > ul > li {
  background: var(--color-background);
  border-radius: 12px;
  padding: 12px;
  cursor: pointer;
  display: flex;
  gap: 12px;
  align-items: center;
  transition: all 0.2s;
  position: relative;
}

.content > .right > ul > li:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.content > .right > ul > li.inactive {
  opacity: 0.5;
}

.content > .right > ul > li > .slide-preview {
  width: 100px;
  height: 65px;
  border-radius: 8px;
  overflow: hidden;
  background: var(--color-background-light);
  flex-shrink: 0;
}

.content > .right > ul > li > .slide-preview > img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.content > .right > ul > li > .slide-preview > .icon-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.content > .right > ul > li > .slide-preview > .icon-placeholder > i {
  font-size: 32px;
  color: var(--color-text-third);
}

.content > .right > ul > li > .slide-info {
  flex: 1;
  min-width: 0;
}

.content > .right > ul > li > .slide-info > .order {
  font-size: 12px;
  color: var(--color-brand);
  font-weight: 600;
}

.content > .right > ul > li > .slide-info > h4 {
  margin: 4px 0;
  font-size: 15px;
  color: var(--color-text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.content > .right > ul > li > .slide-info > p {
  margin: 0;
  font-size: 13px;
  color: var(--color-text-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.content > .right > ul > li > .status-dot {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.content > .right > ul > li > .status-dot.active {
  background: #22c55e;
}

.content > .right > ul > li > .status-dot.inactive {
  background: var(--color-text-third);
}

/* Empty states */
.empty,
.empty-state {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: var(--color-text-third);
}

.empty > p,
.empty-state > p {
  margin: 0;
  text-align: center;
  line-height: 1.5;
}

.empty-state > i {
  font-size: 48px;
}

/* 반응형 */
@media (max-width: 768px) {
  .content {
    flex-direction: column;
  }

  .content > .left {
    width: 100%;
    height: auto;
    max-height: 40%;
  }

  .content > .right {
    width: 100%;
  }
}
</style>